import { foldStore, isVisitOpen, USER_REF_FIELDS, type EventRow, type Id, type OrderEvent, type StoreModel } from '@odhaczka/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { getDeviceId, nextSeq } from '../db/clock';
import { commit, newId, type Writer } from '../db/commit';
import { db } from '../db/db';
import type { ListItemRow, ListRow, VisitRow } from '../db/types';
import { currentUserId } from '../lib/auth';
import { maybeSampleVisit } from './geo';
import { buildMergeMap, resolveWith } from './products';
import { loadStoreResolver } from './stores';

export type CheckStatus = Exclude<ListItemRow['status'], 'pending'>;

/**
 * Model kolejności sklepu przeliczany z dziennika zdarzeń (działa offline).
 * Sklep scalony z innymi składa historię wszystkich duplikatów w jedną trasę.
 */
export async function loadStoreModel(storeId: Id): Promise<StoreModel> {
  const [stores, products] = await Promise.all([db.stores.toArray(), db.products.toArray()]);
  const resolveStore = resolveWith(buildMergeMap(stores));
  const target = resolveStore(storeId);
  const aliases = [target, ...stores.filter((s) => s.id !== target && resolveStore(s.id) === target).map((s) => s.id)];
  const events = await db.orderEvents.where('storeId').anyOf(aliases).toArray();
  return foldStore(target, events as unknown as OrderEvent[], {
    resolveProduct: resolveWith(buildMergeMap(products)),
    resolveStore,
  });
}

export function useStoreModel(storeId: Id | null | undefined): StoreModel | null | undefined {
  return useLiveQuery(async () => (storeId ? loadStoreModel(storeId) : null), [storeId]);
}

export function useActiveVisit(listId: Id | undefined): VisitRow | null | undefined {
  return useLiveQuery(async () => (listId ? ((await findActiveVisit(listId)) ?? null) : null), [listId]);
}

async function findActiveVisit(listId: Id): Promise<VisitRow | undefined> {
  const me = getDeviceId();
  const visits = await db.visits.where('listId').equals(listId).toArray();
  return visits
    .filter((v) => !v.deleted && v.deviceId === me && v.finishedMs === null)
    .sort((a, b) => b.startedMs - a.startedMs)[0];
}

async function finishVisit(w: Writer, visit: VisitRow, now: number) {
  await w.set('visits', visit.id, { finishedMs: now });
  // Wiersz wizyty bez deviceId (niepełna synchronizacja) nie może zablokować zakończenia zakupów walidacją zdarzenia.
  await w.append({ storeId: visit.storeId, kind: 'visit_finished', visitId: visit.id, deviceId: visit.deviceId || getDeviceId() });
}

/**
 * Wizyta w sklepie listy: bieżąca albo nowa (stara kończy się po przerwie lub zmianie sklepu).
 * storeId = sklep listy po scaleniach.
 */
async function ensureVisit(w: Writer, list: ListRow, storeId: Id, now: number): Promise<VisitRow> {
  const active = await findActiveVisit(list.id);
  if (active) {
    const resolveStore = await loadStoreResolver();
    // Po 2 h bez moich odhaczeń (isVisitOpen) kolejne zakupy to nowa wizyta.
    const items = await db.listItems.where('listId').equals(active.listId).toArray();
    const stale = resolveStore(active.storeId) !== storeId || !isVisitOpen(active, items, now);
    if (!stale) return active;
    await finishVisit(w, active, now);
  }
  const visit: VisitRow = {
    id: newId(),
    fieldHlc: {},
    storeId,
    listId: list.id,
    deviceId: getDeviceId(),
    userId: currentUserId(),
    startedMs: now,
    finishedMs: null,
  };
  await w.set('visits', visit.id, {
    storeId: visit.storeId,
    listId: visit.listId,
    deviceId: visit.deviceId,
    userId: visit.userId ?? null,
    startedMs: visit.startedMs,
    finishedMs: null,
  });
  return visit;
}

export interface UndoToken {
  itemId: Id;
  previous: Pick<ListItemRow, 'status' | 'checkedMs' | 'partialQty' | 'substitute' | 'note' | 'checkedBy'> &
    Partial<Pick<ListItemRow, 'suggestion' | 'suggestedBy' | 'suggestedMs'>>;
  /** Zdarzenie odhaczenia do cofnięcia w dzienniku kolejności (jeśli powstało). */
  event: { id: Id; storeId: Id; visitId: Id } | null;
}

export interface CheckExtra {
  partialQty?: number | null;
  substitute?: string | null;
  note?: string | null;
}

/**
 * Odhacza pozycję (w koszyku / brak / częściowo / zamiennik).
 * Pierwsze odhaczenie w wizycie trafia do dziennika kolejności — to z niego aplikacja uczy się trasy.
 */
export async function checkItem(itemId: Id, status: CheckStatus, extra: CheckExtra = {}): Promise<UndoToken> {
  return sampleAfter(await commit((w) => checkIn(w, itemId, status, extra)));
}

/** Po zatwierdzonym odhaczeniu, w tle: próbka położenia sklepu z wizyty (GPS nie może czekać w transakcji). */
function sampleAfter(token: UndoToken): UndoToken {
  if (token.event) void maybeSampleVisit(token.event.visitId).catch(() => {});
  return token;
}

/** Wspólna ścieżka odhaczenia; `more` = dodatkowe pola zapisywane razem (np. wyczyszczenie propozycji). */
async function checkIn(w: Writer, itemId: Id, status: CheckStatus, extra: CheckExtra, more: Partial<ListItemRow> = {}): Promise<UndoToken> {
  const item = await db.listItems.get(itemId);
  if (!item) throw new Error('Nie ma takiej pozycji');
  const list = await db.lists.get(item.listId);
  if (!list) throw new Error('Nie ma takiej listy');
  const now = Date.now();
  const previous: UndoToken['previous'] = {
    status: item.status,
    checkedMs: item.checkedMs,
    partialQty: item.partialQty,
    substitute: item.substitute,
    note: item.note,
    checkedBy: item.checkedBy ?? null,
  };
  if ('suggestion' in more) {
    previous.suggestion = item.suggestion ?? null;
    previous.suggestedBy = item.suggestedBy ?? null;
    previous.suggestedMs = item.suggestedMs ?? null;
  }
  const fields: Partial<ListItemRow> = { status, checkedMs: item.checkedMs ?? now, checkedBy: currentUserId(), ...more };
  if (extra.partialQty !== undefined) fields.partialQty = extra.partialQty;
  if (extra.substitute !== undefined) fields.substitute = extra.substitute;
  if (extra.note !== undefined) fields.note = extra.note;
  if (status !== 'partial' && extra.partialQty === undefined) fields.partialQty = null;
  if (status !== 'substitute' && extra.substitute === undefined) fields.substitute = null;
  await w.set('listItems', itemId, fields);

  let event: UndoToken['event'] = null;
  if (item.status === 'pending' && item.productId && list.storeId) {
    const storeId = (await loadStoreResolver())(list.storeId);
    const visit = await ensureVisit(w, list, storeId, now);
    const ev = await w.append({
      storeId,
      kind: 'item_checked',
      visitId: visit.id,
      listItemId: itemId,
      productId: item.productId,
      status,
      seq: await nextSeq(),
      wallMs: now,
      deviceId: getDeviceId(),
    });
    event = { id: ev.id, storeId, visitId: visit.id };
  }
  return { itemId, previous, event };
}

/** Zamiennik zaproponowany z domu (null = wycofanie propozycji). Robiący zakupy widzi go przy pozycji. */
export async function suggestSubstitute(itemId: Id, text: string | null): Promise<void> {
  const suggestion = text?.trim() || null;
  await commit((w) =>
    w.set(
      'listItems',
      itemId,
      suggestion
        ? { suggestion, suggestedBy: currentUserId(), suggestedMs: Date.now() }
        : { suggestion: null, suggestedBy: null, suggestedMs: null },
    ),
  );
}

/**
 * „Biorę”: pozycja odhaczona jako zamiennik z treścią propozycji (ta sama ścieżka co checkItem —
 * nieodhaczona pozycja trafia do nauki trasy), a propozycja znika. Cofnięcie przywraca też propozycję.
 */
export async function acceptSuggestion(itemId: Id): Promise<UndoToken> {
  const token = await commit(async (w) => {
    const item = await db.listItems.get(itemId);
    if (!item?.suggestion) throw new Error('Ta propozycja jest już nieaktualna');
    return checkIn(w, itemId, 'substitute', { substitute: item.suggestion }, { suggestion: null, suggestedBy: null, suggestedMs: null });
  });
  return sampleAfter(token);
}

/** „Nie ma”: odrzuca propozycję zamiennika, pozycja zostaje bez zmian. */
export async function rejectSuggestion(itemId: Id): Promise<void> {
  await commit((w) => w.set('listItems', itemId, { suggestion: null, suggestedBy: null, suggestedMs: null }));
}

/**
 * Pola do przywrócenia przy „Cofnij”. Serwer przyjmuje w polach *By tylko null albo zalogowanego
 * użytkownika (inaczej odrzuca całą zmianę), więc cudzego podpisu nie przywracamy — zostaje bieżący.
 */
function restorable<T extends Partial<ListItemRow>>(previous: T): T {
  const me = currentUserId();
  const out: Partial<Record<string, unknown>> = { ...previous };
  for (const f of USER_REF_FIELDS) if (out[f] != null && out[f] !== me) delete out[f];
  return out as T;
}

export async function undoCheck(token: UndoToken): Promise<void> {
  await commit(async (w) => {
    await w.set('listItems', token.itemId, restorable(token.previous));
    if (token.event) {
      await w.append({
        storeId: token.event.storeId,
        kind: 'undo',
        visitId: token.event.visitId,
        targetId: token.event.id,
        deviceId: getDeviceId(),
      });
    }
  });
}

export interface UncheckToken {
  itemId: Id;
  previous: UndoToken['previous'];
  /** Cofnięte odhaczenie — przy „Cofnij” wraca z tym samym numerem kolejnym, więc trasa się nie zmienia. */
  undone: EventRow | null;
}

/** Ostatnie niecofnięte odhaczenie pozycji w dzienniku kolejności. */
async function lastCheck(itemId: Id): Promise<EventRow | undefined> {
  const events = await db.orderEvents.toArray();
  const undoneIds = new Set(events.filter((e) => e.kind === 'undo').map((e) => e.targetId as string));
  return events
    .filter((e) => e.kind === 'item_checked' && e.listItemId === itemId && !undoneIds.has(e.id))
    .sort((a, b) => (a.hlc < b.hlc ? 1 : -1))[0];
}

/**
 * Pozycja dostała inny produkt (poprawiona nazwa): odhaczenie w trwającej wizycie przechodzi na nowy produkt
 * z tym samym numerem kolejnym, więc trasa uczy się właściwego produktu w tym samym miejscu.
 * Zakończona wizyta już się nauczyła — jej nie ruszamy.
 */
export async function repointCheck(w: Writer, itemId: Id, productId: Id): Promise<void> {
  const last = await lastCheck(itemId);
  if (!last || last.productId === productId) return;
  const visit = await db.visits.get(last.visitId as string);
  if (!visit || visit.finishedMs !== null) return;
  await w.append({ storeId: last.storeId, kind: 'undo', visitId: last.visitId as string, targetId: last.id, deviceId: getDeviceId() });
  const { id: _id, hlc: _hlc, ...rest } = last;
  await w.append({ ...rest, productId });
}

/** Przywraca pozycję do „do kupienia” (cofa też ostatnie odhaczenie w dzienniku). */
export async function uncheckItem(itemId: Id): Promise<UncheckToken> {
  return commit(async (w) => {
    const item = await db.listItems.get(itemId);
    if (!item) throw new Error('Nie ma takiej pozycji');
    const last = await lastCheck(itemId);
    const previous = {
      status: item.status,
      checkedMs: item.checkedMs,
      partialQty: item.partialQty,
      substitute: item.substitute,
      note: item.note,
      checkedBy: item.checkedBy ?? null,
    };
    await w.set('listItems', itemId, { status: 'pending', checkedMs: null, checkedBy: null, partialQty: null, substitute: null });
    if (last) {
      await w.append({
        storeId: last.storeId,
        kind: 'undo',
        visitId: last.visitId as string,
        targetId: last.id,
        deviceId: getDeviceId(),
      });
    }
    return { itemId, previous, undone: last ?? null };
  });
}

/** Cofa „przywróć do kupienia”: pozycja wraca do poprzedniego stanu, a odhaczenie na swoje miejsce w trasie. */
export async function undoUncheck(token: UncheckToken): Promise<void> {
  await commit(async (w) => {
    await w.set('listItems', token.itemId, restorable(token.previous));
    if (token.undone) {
      const { id: _id, hlc: _hlc, ...rest } = token.undone;
      await w.append(rest);
    }
  });
}

/**
 * Oznacza wszystkie niekupione pozycje jako brak — bez zdarzeń nauki,
 * bo przy tych półkach nikt nie był (masowe oznaczenie przy kasie).
 */
export async function markRemainingMissing(listId: Id): Promise<void> {
  await commit(async (w) => {
    const now = Date.now();
    const items = await db.listItems.where('listId').equals(listId).toArray();
    for (const i of items) {
      if (!i.deleted && i.status === 'pending') await w.set('listItems', i.id, { status: 'missing', checkedMs: now, checkedBy: currentUserId() });
    }
  });
}

export async function setItemNote(itemId: Id, note: string | null): Promise<void> {
  await commit((w) => w.set('listItems', itemId, { note: note?.trim() || null }));
}

/**
 * Kończy zakupy: zamyka wizyty listy (to uruchamia naukę kolejności) i oznacza listę jako zrobioną.
 * Zamyka otwarte wizyty WSZYSTKICH urządzeń — inaczej odhaczenia drugiej osoby nie trafiłyby do nauki.
 */
export async function finishShopping(listId: Id): Promise<void> {
  await commit(async (w) => {
    const now = Date.now();
    const open = (await db.visits.where('listId').equals(listId).toArray()).filter((v) => !v.deleted && v.finishedMs === null);
    for (const v of open) await finishVisit(w, v, now);
    await w.set('lists', listId, { status: 'done', finishedMs: now });
  });
}

/**
 * Zamyka wizyty, których lista nie jest już aktywna (zakończyło je inne urządzenie, gdy to było offline).
 * Wołane po każdej synchronizacji.
 */
export async function closeStaleVisits(): Promise<void> {
  const open = (await db.visits.toArray()).filter((v) => !v.deleted && v.finishedMs === null);
  if (open.length === 0) return;
  const stale: VisitRow[] = [];
  for (const v of open) {
    const list = await db.lists.get(v.listId);
    if (!list || list.deleted || list.status !== 'active') stale.push(v);
  }
  if (stale.length === 0) return;
  await commit(async (w) => {
    const now = Date.now();
    for (const v of stale) await finishVisit(w, v, now);
  });
}

/** Wznawia zakończoną listę (np. omyłkowe „Zakończ”). */
export async function reopenList(listId: Id): Promise<void> {
  await commit((w) => w.set('lists', listId, { status: 'active', finishedMs: null }));
}

/**
 * Ręczne przestawienie produktu w sklepie. afterId/beforeId = produkty bezpośrednio nad i pod
 * po upuszczeniu (null na początku/końcu listy).
 */
export async function manualMove(storeId: Id, productId: Id, afterId: Id | null, beforeId: Id | null): Promise<void> {
  const resolveStore = await loadStoreResolver();
  await commit(async (w) => {
    await w.append({ storeId: resolveStore(storeId), kind: 'manual_move', productId, afterId, beforeId, deviceId: getDeviceId() });
  });
}

/**
 * Kopiuje pozycje (np. braki) na inną listę jako „do kupienia”.
 * Pomija te, które już tam czekają (ten sam produkt albo ta sama nazwa) — ponowne przeniesienie nic nie dubluje.
 * Zwraca liczbę faktycznie dodanych pozycji.
 */
export async function copyItemsToList(items: ListItemRow[], listId: Id): Promise<number> {
  const existing = (await db.listItems.where('listId').equals(listId).toArray()).filter((i) => !i.deleted);
  const waiting = new Set(
    existing.filter((i) => i.status === 'pending').map((i) => i.productId ?? `name:${i.name.toLowerCase()}`),
  );
  const toAdd = items.filter((i) => !waiting.has(i.productId ?? `name:${i.name.toLowerCase()}`));
  let position = existing.reduce((m, i) => Math.max(m, i.position), -1) + 1;
  await commit(async (w) => {
    for (const i of toAdd) {
      const remaining = i.status === 'partial' && i.qty !== null && i.partialQty !== null ? Math.max(i.qty - i.partialQty, 1) : i.qty;
      await w.set('listItems', newId(), {
        listId,
        rawText: i.rawText,
        name: i.name,
        productId: i.productId,
        qty: remaining,
        unit: i.unit,
        amountValue: i.amountValue,
        amountUnit: i.amountUnit,
        position: position++,
        status: 'pending',
        note: i.note,
        partialQty: null,
        substitute: null,
        checkedMs: null,
        addedBy: currentUserId(),
        addedMs: Date.now(),
      });
    }
  });
  return toAdd.length;
}

/** Lista docelowa dla przeniesionych braków: najnowsza aktywna (inna niż bieżąca) albo nowa. */
export async function nextListFor(excludeListId: Id, storeId: Id | null = null): Promise<Id> {
  const resolveStore = await loadStoreResolver();
  const sameStore = (id: Id | null) => storeId === null || (id !== null && resolveStore(id) === resolveStore(storeId));
  const active = (await db.lists.toArray())
    .filter((l) => !l.deleted && l.status === 'active' && l.id !== excludeListId && sameStore(l.storeId))
    .sort((a, b) => (b.createdMs ?? 0) - (a.createdMs ?? 0))[0];
  if (active) return active.id;
  const id = newId();
  await commit((w) =>
    w.set('lists', id, {
      title: storeId ? 'Do dokupienia' : 'Następne zakupy',
      storeId,
      status: 'active',
      createdMs: Date.now(),
      finishedMs: null,
    }),
  );
  return id;
}

/** Tekst braków do wysłania (SMS/komunikator). */
export function missingText(items: ListItemRow[]): string {
  const lines = items.map((i) => {
    let s = `- ${i.name}`;
    if (i.status === 'partial' && i.partialQty !== null && i.qty !== null) s += ` (jest ${i.partialQty} z ${i.qty})`;
    if (i.status === 'substitute' && i.substitute) s += ` → zamiast tego: ${i.substitute}`;
    if (i.note) s += ` — ${i.note}`;
    return s;
  });
  return `Nie było:\n${lines.join('\n')}`;
}
